import { z } from 'zod'
import { InvoiceSchema, PartialAddressSchema } from './AddressesSchema'
import { prescriptionResponseSchema } from './refraction'
import {
  PaginationOptionsSchema,
  makeErpPaginatedSchema,
} from '../types/api/pagination'

export const CustomerSchema = z.object({
  first_name: z.string(),
  last_name: z.string(),
  ref: z.string(),
  email: z.union([z.string().email(), z.literal('')]).nullish(),
  mobile: z.string().nullish(),
  /**
   * Landline number
   */
  phone: z.string().nullish(),
  // prefix: z.string().nullable().openapi({
  //   example: '+49',
  // }),
  birth_date: z.string().nullish(),
  referrer_id: z.string().nullish(),
  address: PartialAddressSchema,
  email_state: z.boolean(),
  phone_state: z.boolean(),
  invoice: InvoiceSchema.optional(),
  credit: z.number().default(0),
  title: z.string().nullish(),
  is_kids: z.boolean().nullish(),
})

export const CustomerCreateSchema = CustomerSchema.omit({
  phone_state: true,
  email_state: true,
  credit: true,
  ref: true,
}).extend({})

export type CustomerCreatePayload = z.infer<typeof CustomerCreateSchema>

export const CustomerWithIdSchema = CustomerSchema.extend({
  id: z.number(),
})

export const CustomerPatchSchema = CustomerSchema.omit({
  credit: true,
  referrer_id: true,
  email_state: true,
  phone_state: true,
}).partial()

export type CustomerPatchSchemaType = z.infer<typeof CustomerPatchSchema>

export const PhoneCodeVerifySchema = z.object({
  type: z.literal('phone'),
  phone: z.string(),
  prefix: z.string(),
  code: z.string(),
})

export const EmailCodeVerifySchema = z.object({
  type: z.literal('email'),
  email: z.string().email(),
  code: z.string(),
})

export const CodeVerifySchema = z.union([
  PhoneCodeVerifySchema,
  EmailCodeVerifySchema,
])

export type CodeVerifySchema = z.infer<typeof CodeVerifySchema>

export const PhoneVerificationSchema = z.object({
  type: z.literal('phone'),
  phone: z.string(),
  prefix: z.string(),
})

export const EmailVerificationSchema = z.object({
  type: z.literal('email'),
  email: z.string().email(),
})

export const AuthSchema = z.object({
  type: z.enum(['email', 'phone']),
  username: z.string(),
  password: z.string(),
})

export const VerificationSchema = z.union([
  EmailVerificationSchema,
  PhoneVerificationSchema,
])

export const CustomerListSchema = makeErpPaginatedSchema(CustomerWithIdSchema)

export const CustomerListQuerySchema = z
  .object({
    // HACK: String with space is a hack here to match all customers
    query: z.string().default(' '),
  })
  .extend(PaginationOptionsSchema.shape)

export type CustomerListQuery = z.infer<typeof CustomerListQuerySchema>

export const CustomerPrescriptionsSchema = makeErpPaginatedSchema(
  prescriptionResponseSchema
)

export const CustomerDetailSectionSchema = z.enum([
  'personal',
  'contact',
  'address',
  'invoice',
  'policies',
])

export type CustomerDetailSection = z.infer<typeof CustomerDetailSectionSchema>

export const SessionInfoSchema = z.object({
  hasEyeTest: z.boolean().optional(),
  isCustomerDetailsConfirmed: z.boolean().optional(),
  savedDetailSections: z.array(CustomerDetailSectionSchema).optional(),
  lastUsedPrescriptionId: z.number().optional(),
  lastUsedPd: z
    .object({
      r_pd: z.number(),
      l_pd: z.number(),
    })
    .optional(),
  lastUsedPdOption: z
    .enum(['prescription', 'manual', 'latestOrder', 'measurement'])
    .optional(),
})

export type SessionInfo = z.infer<typeof SessionInfoSchema>

export const CustomerPolicyStateSchema = z.object({
  required: z.boolean(),
  revision: z.string(),
  acceptedAt: z.iso.datetime().nullable(),
})

export const CustomerPoliciesStateSchema = z.record(
  z.string(),
  CustomerPolicyStateSchema
)

export const CustomerPolicyPatchSchema = z.record(z.string(), z.boolean())

export type CustomerPolicyState = z.infer<typeof CustomerPolicyStateSchema>
export type CustomerPoliciesState = z.infer<typeof CustomerPoliciesStateSchema>
export type CustomerPolicyPatch = z.infer<typeof CustomerPolicyPatchSchema>

export const CustomerInfoSchema = CustomerWithIdSchema.extend({
  info: SessionInfoSchema.optional(),
})

export type CustomerInfo = z.infer<typeof CustomerInfoSchema>
